import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { ApiError } from "../api/client";
import { createActivity, getActivity, updateActivity } from "../api/activities";
import { listAcademicYears, listCategories, listStudyPrograms, listUnits } from "../api/master";
import { useAuth } from "../hooks/useAuth";
import {
  buildActivityPayload,
  canPickProgram,
  emptyActivityForm,
  filterUnitsByProgram,
  type ActivityFormState,
} from "../lib/activity";
import { toDateInputValue } from "../lib/format";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../components/ui";

/** Formulir catat/ubah kegiatan (rute /kegiatan/baru dan /kegiatan/:id/ubah). */
export function ActivityFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [form, setForm] = useState<ActivityFormState>(() => emptyActivityForm());
  const [error, setError] = useState<string | null>(null);

  const mayPickProgram = canPickProgram(user?.role);

  const categoriesQuery = useQuery({ queryKey: ["master", "categories"], queryFn: listCategories });
  const yearsQuery = useQuery({
    queryKey: ["master", "academic-years"],
    queryFn: listAcademicYears,
  });
  const programsQuery = useQuery({
    queryKey: ["master", "study-programs"],
    queryFn: listStudyPrograms,
  });
  const unitsQuery = useQuery({ queryKey: ["master", "units"], queryFn: listUnits });

  const detailQuery = useQuery({
    queryKey: ["activity", id],
    queryFn: () => getActivity(id ?? ""),
    enabled: isEdit,
  });

  const years = yearsQuery.data?.items ?? [];
  const activeYear = useMemo(() => years.find((year) => year.isActive) ?? years[0], [years]);
  const semesters = activeYear?.semesters ?? [];
  const units = unitsQuery.data?.items ?? [];

  useEffect(() => {
    if (!isEdit) return;
    const activity = detailQuery.data;
    if (!activity) return;
    setForm({
      title: activity.title,
      description: activity.description,
      activityDate: toDateInputValue(activity.activityDate),
      startTime: activity.startTime ?? "",
      endTime: activity.endTime ?? "",
      location: activity.location,
      categoryId: activity.categoryId,
      academicYearId: activity.academicYearId,
      semesterId: activity.semesterId,
      organizer: activity.organizer,
      responsiblePerson: activity.responsiblePerson,
      output: activity.output ?? "",
      outcome: activity.outcome ?? "",
      externalLink: activity.externalLink ?? "",
      studyProgramId: activity.studyProgramId,
      unitId: activity.unitId ?? "",
    });
  }, [isEdit, detailQuery.data]);

  useEffect(() => {
    if (isEdit || form.academicYearId || years.length === 0) return;
    setForm((current) => ({
      ...current,
      academicYearId: activeYear?.id ?? "",
      semesterId: activeYear?.semesters[0]?.id ?? "",
    }));
  }, [activeYear, form.academicYearId, isEdit, years.length]);

  const availableUnits = useMemo(
    () => filterUnitsByProgram(units, form.studyProgramId),
    [units, form.studyProgramId],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildActivityPayload(form, { canPickProgram: mayPickProgram });
      return isEdit && id ? updateActivity(id, payload) : createActivity(payload);
    },
    onSuccess: (activity) => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      navigate(`/kegiatan/${activity.id}`, { replace: true });
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Kegiatan gagal disimpan.");
    },
  });

  function update<K extends keyof ActivityFormState>(key: K, value: ActivityFormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  if (isEdit && detailQuery.isLoading) return <Spinner label="Memuat kegiatan..." />;
  if (isEdit && detailQuery.data && !detailQuery.data.permissions.canEdit) {
    return (
      <Alert tone="error">
        Kegiatan ini tidak dapat diubah pada status saat ini. Hanya draf atau kegiatan yang ditolak
        yang dapat diubah.
      </Alert>
    );
  }

  const loading =
    categoriesQuery.isLoading ||
    yearsQuery.isLoading ||
    programsQuery.isLoading ||
    unitsQuery.isLoading;

  return (
    <div className="space-y-5">
      <Link to={isEdit && id ? `/kegiatan/${id}` : "/kegiatan"} className="inline-block">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
          <ArrowLeft className="size-4" aria-hidden />
          Kembali
        </span>
      </Link>

      <PageHeader
        title={isEdit ? "Ubah kegiatan" : "Catat kegiatan baru"}
        description="Isi data kegiatan sesuai dokumen pendukung. Kolom bertanda * wajib diisi."
      />

      {error ? <Alert tone="error">{error}</Alert> : null}

      {loading ? (
        <Spinner label="Memuat data master..." />
      ) : (
        <Card>
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              setError(null);
              saveMutation.mutate();
            }}
          >
            <div className="sm:col-span-2">
              <Field label="Judul kegiatan *" htmlFor="title">
                <Input
                  id="title"
                  value={form.title}
                  onChange={(event) => update("title", event.target.value)}
                  minLength={3}
                  maxLength={200}
                  required
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="Deskripsi *" htmlFor="description" hint="Minimal 10 karakter.">
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(event) => update("description", event.target.value)}
                  minLength={10}
                  maxLength={2000}
                  required
                />
              </Field>
            </div>

            <Field label="Tanggal pelaksanaan *" htmlFor="activityDate">
              <Input
                id="activityDate"
                type="date"
                value={form.activityDate}
                onChange={(event) => update("activityDate", event.target.value)}
                required
              />
            </Field>

            <Field label="Lokasi *" htmlFor="location">
              <Input
                id="location"
                value={form.location}
                onChange={(event) => update("location", event.target.value)}
                required
              />
            </Field>

            <Field label="Waktu mulai" htmlFor="startTime">
              <Input
                id="startTime"
                type="time"
                value={form.startTime}
                onChange={(event) => update("startTime", event.target.value)}
              />
            </Field>

            <Field label="Waktu selesai" htmlFor="endTime">
              <Input
                id="endTime"
                type="time"
                value={form.endTime}
                onChange={(event) => update("endTime", event.target.value)}
              />
            </Field>

            <Field label="Kategori *" htmlFor="categoryId">
              <Select
                id="categoryId"
                value={form.categoryId}
                onChange={(event) => update("categoryId", event.target.value)}
                required
              >
                <option value="">Pilih kategori</option>
                {(categoriesQuery.data?.items ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Tahun akademik *" htmlFor="academicYearId">
              <Select
                id="academicYearId"
                value={form.academicYearId}
                onChange={(event) => {
                  const year = years.find((item) => item.id === event.target.value);
                  setForm((current) => ({
                    ...current,
                    academicYearId: event.target.value,
                    semesterId: year?.semesters[0]?.id ?? "",
                  }));
                }}
                required
              >
                <option value="">Pilih tahun akademik</option>
                {years.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Semester *" htmlFor="semesterId">
              <Select
                id="semesterId"
                value={form.semesterId}
                onChange={(event) => update("semesterId", event.target.value)}
                required
              >
                <option value="">Pilih semester</option>
                {semesters.map((semester) => (
                  <option key={semester.id} value={semester.id}>
                    {semester.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Penyelenggara *" htmlFor="organizer">
              <Input
                id="organizer"
                value={form.organizer}
                onChange={(event) => update("organizer", event.target.value)}
                required
              />
            </Field>

            <Field label="Penanggung jawab *" htmlFor="responsiblePerson">
              <Input
                id="responsiblePerson"
                value={form.responsiblePerson}
                onChange={(event) => update("responsiblePerson", event.target.value)}
                required
              />
            </Field>

            {mayPickProgram ? (
              <Field
                label="Program studi"
                htmlFor="studyProgramId"
                hint="Kosongkan untuk memakai program studi akun Anda."
              >
                <Select
                  id="studyProgramId"
                  value={form.studyProgramId}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      studyProgramId: event.target.value,
                      unitId: "",
                    }));
                  }}
                >
                  <option value="">Ikuti akun saya</option>
                  {(programsQuery.data?.items ?? []).map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            <Field label="Unit pelaksana" htmlFor="unitId">
              <Select
                id="unitId"
                value={form.unitId}
                onChange={(event) => update("unitId", event.target.value)}
              >
                <option value="">Tanpa unit</option>
                {availableUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Luaran" htmlFor="output">
              <Input
                id="output"
                value={form.output}
                onChange={(event) => update("output", event.target.value)}
              />
            </Field>

            <Field label="Hasil / dampak" htmlFor="outcome">
              <Input
                id="outcome"
                value={form.outcome}
                onChange={(event) => update("outcome", event.target.value)}
              />
            </Field>

            <div className="sm:col-span-2">
              <Field
                label="Tautan pendukung"
                htmlFor="externalLink"
                hint="Diawali http:// atau https://"
              >
                <Input
                  id="externalLink"
                  type="url"
                  value={form.externalLink}
                  onChange={(event) => update("externalLink", event.target.value)}
                  placeholder="https://"
                />
              </Field>
            </div>

            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" loading={saveMutation.isPending}>
                <Save className="size-4" aria-hidden />
                {isEdit ? "Simpan perubahan" : "Simpan kegiatan"}
              </Button>
              <Link to={isEdit && id ? `/kegiatan/${id}` : "/kegiatan"}>
                <Button type="button" variant="secondary">
                  Batal
                </Button>
              </Link>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
